import type { AddOn, ConfiguredAddOn } from '../types/addon.types';
import type { ConfiguredService } from '../types/service.types';
import { toLineDescription } from './lineDescription';
import type { InstanceIdMinter } from './instanceIds';

const resolveTargetServiceIds = (assignment: AddOn, services: ConfiguredService[]): string[] => {
    if (assignment.auto_include_link === 'eachservice') {
        return services.map((service) => service.instanceId);
    }
    const position = Number(assignment.auto_include_link);
    if (!Number.isInteger(position) || position < 1) {
        return [];
    }
    const service = services[position - 1];
    return service ? [service.instanceId] : [];
};

interface ReconcileAutoIncludedAddOnsInput {
    assignments: AddOn[];
    services: ConfiguredService[];
    addOns: ConfiguredAddOn[];
    dismissedAutoAddOnIds: string[];
    nextInstanceId: InstanceIdMinter;
}

// Lines are never removed here (pruneOrphanedAddOns owns that); returns null
// when nothing changes so the caller never dispatches on an unchanged reconcile.
export function reconcileAutoIncludedAddOns({
    assignments,
    services,
    addOns,
    dismissedAutoAddOnIds,
    nextInstanceId,
}: ReconcileAutoIncludedAddOnsInput): ConfiguredAddOn[] | null {
    const next = [...addOns];
    let changed = false;

    for (const assignment of assignments) {
        if (!assignment.auto_include) continue;

        for (const linkedServiceId of resolveTargetServiceIds(assignment, services)) {
            const existingIndex = next.findIndex(
                (addOn) =>
                    addOn.addOnId === assignment.id && addOn.linkedServiceId === linkedServiceId
            );

            if (existingIndex !== -1) {
                const existing = next[existingIndex];
                if (assignment.mandatory && !existing.mandatory) {
                    next[existingIndex] = { ...existing, mandatory: true, source: 'auto' };
                    changed = true;
                }
                continue;
            }

            if (dismissedAutoAddOnIds.includes(assignment.id) && !assignment.mandatory) {
                continue;
            }

            next.push({
                instanceId: nextInstanceId(),
                addOnId: assignment.id,
                name: assignment.name,
                description: toLineDescription(assignment.description),
                price: assignment.price,
                gross_price: assignment.gross_price,
                linkedServiceId,
                startDate: null,
                source: 'auto',
                mandatory: assignment.mandatory,
            });
            changed = true;
        }
    }

    return changed ? next : null;
}

// Keeps the manual-add form from offering an add-on that already has a
// mandatory line for the link the agent has selected.
export function isMandatoryAddOnLineForLink(
    addOnId: string,
    linkedServiceId: string,
    configuredAddOns: ConfiguredAddOn[]
): boolean {
    if (linkedServiceId === 'independent') {
        return false;
    }
    if (linkedServiceId === 'eachservice') {
        return configuredAddOns.some((addOn) => addOn.addOnId === addOnId && addOn.mandatory);
    }
    return configuredAddOns.some(
        (addOn) =>
            addOn.addOnId === addOnId &&
            addOn.mandatory &&
            addOn.linkedServiceId === linkedServiceId
    );
}

// Runs once per quote load. Only an assignment that resolves to a target today
// can have been removed before saving, so an unseedable one is never dismissed.
export function loadBaselineDismissals(
    assignments: AddOn[],
    services: ConfiguredService[],
    addOns: ConfiguredAddOn[]
): string[] {
    const linkedAddOnIds = new Set(addOns.map((addOn) => addOn.addOnId));

    return assignments
        .filter(
            (assignment) =>
                assignment.auto_include &&
                !assignment.mandatory &&
                !linkedAddOnIds.has(assignment.id) &&
                resolveTargetServiceIds(assignment, services).length > 0
        )
        .map((assignment) => assignment.id);
}
